import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { springs } from '../lib/animations';
import Avatar from './Avatar';

export interface FeedItem {
  id: number;
  roller_name: string;
  final_score: number | null;
  verdict: string;
  image_path: string | null;
  created_at: string;
  strain_name: string | null;
  fire_count: number;
  trash_count: number;
  respect_count: number;
  comment_count: number;
}

function relativeTime(dateStr: string): string {
  const now = Date.now();
  const then = new Date(dateStr).getTime();
  const diff = Math.max(0, now - then);
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
}

function scoreColor(score: number): string {
  if (score < 4) return 'text-score-low';
  if (score <= 7) return 'text-warm';
  return 'text-score-high';
}

export default function FeedCard({ item }: { item: FeedItem }) {
  const navigate = useNavigate();
  return (
    <motion.div
      whileHover={{ y: -2, boxShadow: '0 4px 16px rgba(212,165,116,0.15)' }}
      transition={springs.subtle}
    >
      <Link
        to={`/rating/${item.id}`}
        className="block bg-cannabis-800 rounded-2xl border border-cannabis-700/30 hover:border-warm/20 p-3 shadow-card"
      >
        {/* Top row: avatar + name + timestamp */}
        <div className="flex items-center gap-2 mb-2">
          <Avatar username={item.roller_name} size="sm" />
          <div className="flex-1 min-w-0">
            <span
              role="link"
              className="text-cream font-semibold text-xs hover:underline cursor-pointer"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigate(`/profile/${encodeURIComponent(item.roller_name)}`);
              }}
            >
              {item.roller_name}
            </span>
            {item.strain_name && (
              <span className="text-cannabis-400 text-[11px] ml-1.5">
                {item.strain_name}
              </span>
            )}
          </div>
          <span className="text-cream/25 text-[11px] shrink-0">{relativeTime(item.created_at)}</span>
        </div>

        {/* Joint image */}
        {item.image_path && (
          <img
            src={item.image_path}
            alt="Joint"
            className="w-full max-h-[160px] object-cover rounded-xl mb-2"
          />
        )}

        {/* Score + verdict */}
        <div className="flex items-start gap-2 mb-2">
          <span className={`text-2xl font-bold font-mono leading-none ${scoreColor(item.final_score || 5)}`}>
            {(item.final_score || 5).toFixed(1)}
          </span>
          <p className="text-cream/50 italic text-xs line-clamp-2 flex-1 leading-snug">
            "{item.verdict}"
          </p>
        </div>

        {/* Bottom row: reactions + comments */}
        <div className="flex items-center gap-3 text-[11px] text-cream/35">
          <span className="hover:text-cream/60 transition">{item.fire_count} Fire</span>
          <span className="hover:text-cream/60 transition">{item.trash_count} Trash</span>
          <span className="hover:text-cream/60 transition">{item.respect_count} Respect</span>
          <span className="ml-auto hover:text-cream/60 transition">{item.comment_count} Comments</span>
        </div>
      </Link>
    </motion.div>
  );
}
